import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Headset,
  Building2,
  Globe,
  BadgeCheck,
  PhoneCall,
  CheckCircle,
} from "lucide-react";
import heroBg from "@/assets/telefonie-nummers-bg.jpg";
import { nummerTypes } from "@/data/telefonie-nummers";

const fmt = (n: number) => n.toFixed(2).replace(".", ",");

const iconMap: Record<string, typeof MapPin> = {
  regionaal: MapPin,
  "085": Building2,
  "088": BadgeCheck,
  service: Headset,
  internationaal: Globe,
};

const colorMap: Record<string, string> = {
  regionaal: "bg-hero-gradient",
  "085": "bg-rinqel-blue-gradient",
  "088": "bg-rinqel-green-gradient",
  service: "bg-hero-gradient",
  internationaal: "bg-rinqel-blue-gradient",
};

const TelefonieNummers = () => {
  return (
    <>
      {/* Hero — zelfde stijl als functiepagina */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={heroBg}
          alt="Zakelijke telefoonnummers"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25" />

        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/telefonie"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Telefonie
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Transparante prijzen · Geen verborgen kosten
              </span>
            </motion.div>

            <motion.h1
              className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-6xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Nummers voor{" "}
              <span className="text-gradient">elke situatie</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Regionaal, nationaal, servicenummers of internationaal — Rinqel heeft alle nummertypen in huis.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Regionaal vanaf € 2,50 p/m
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                Nummerblokken beschikbaar
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Eigen nummer meenemen
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Number type cards */}
      <section className="py-16 md:py-24 bg-hero-gradient-subtle">
        <div className="container">
          <div className="grid gap-8 lg:grid-cols-2">
            {nummerTypes.map((n) => {
              const Icon = iconMap[n.id] || Globe;
              const color = colorMap[n.id] || "bg-hero-gradient";
              return (
                <div
                  key={n.name}
                  className="flex flex-col rounded-2xl border border-border bg-card shadow-card overflow-hidden"
                >
                  {/* Colored header bar */}
                  <div className={`${color} px-7 py-5`}>
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-white">{n.name}</h2>
                        <p className="text-xs text-white/70">{n.examples}</p>
                        <p className="mt-0.5 text-sm font-medium text-white/90">{n.tagline}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col flex-1 px-7 py-5">
                    <p className="text-sm leading-relaxed text-muted-foreground">{n.description}</p>

                    {/* For who */}
                    <ul className="mt-4 space-y-1.5">
                      {n.forWho.map((w) => (
                        <li key={w} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-rinqel-green" />
                          <span>{w}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Pricing blocks */}
                    <div className="mt-5 grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(n.pricing.length, 2)}, 1fr)` }}>
                      {n.pricing.map((row) => {
                        const isOpAanvraag = row.price === 0 && n.id === "internationaal";
                        return (
                          <div
                            key={row.label}
                            className="rounded-xl border border-border bg-muted/40 p-4"
                          >
                            <p className="text-xs text-muted-foreground mb-1">{row.label}</p>
                            <p className="text-xl font-bold text-foreground">
                              {isOpAanvraag ? "Op aanvraag" : `€ ${fmt(row.price)}`}
                              {row.period && <span className="text-sm font-normal text-muted-foreground ml-1">{row.period}</span>}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {isOpAanvraag ? "Afhankelijk van land" : row.eenmalig > 0 ? `Eenmalig € ${fmt(row.eenmalig)}` : "Geen aansluitkosten"}
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Highlight */}
                    <div className={`mt-4 rounded-xl p-4 ${color}`}>
                      <p className="text-sm font-semibold text-white">✓ {n.highlight}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Eigen nummer meenemen */}
          <div className="mt-12 relative overflow-hidden flex flex-col items-center gap-4 rounded-2xl bg-rinqel-green-gradient p-10 text-center shadow-card">
            <div className="absolute -right-8 -top-8 h-48 w-48 rounded-full bg-white/10" />
            <div className="absolute -left-4 bottom-0 h-32 w-32 rounded-full bg-white/5" />
            <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
              <PhoneCall className="h-7 w-7 text-white" />
            </div>
            <h2 className="relative text-2xl font-bold text-white">Eigen nummer meenemen?</h2>
            <p className="relative max-w-xl text-white/80 text-sm leading-relaxed">
              Heb je al een (zakelijk) nummer en wil je dat niet kwijt? Geen probleem — je bestaande
              nummer(s) draag je gewoon over naar Rinqel. Wij regelen de nummerdraagbaarheid voor je,
              zodat je niets hoeft te veranderen aan je drukwerk of website.
            </p>
            <Button className="relative mt-2 bg-white text-secondary hover:bg-white/90 font-semibold" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Vraag nummerdraagbaarheid aan" trackLocation="telefonie-nummers-portability">
                Vraag nummerdraagbaarheid aan <ArrowRight className="h-4 w-4" />
              </TrackedLink>
            </Button>
          </div>

          {/* CTA */}
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Button variant="hero" size="lg" asChild>
              <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="telefonie-nummers-cta">Bereken je prijs <ArrowRight className="h-4 w-4" /></TrackedLink>
            </Button>
            <Button variant="hero-outline" size="lg" asChild>
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="telefonie-nummers-cta">Neem contact op</TrackedLink>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default TelefonieNummers;
